import axios from 'axios';
import type {
  AddOutOfNetworkSupplierPayload,
  BulkCreateInventoryPayload,
  ExistingInventoryItem,
  InventoryAddLinkResponse,
  InventoryAddPageContext,
  InventoryItemCategory,
  InventoryItemDraft,
  LocationSupplier,
  StorageLocationNode,
  CreateStorageLocationPayload,
  ImportedInventoryItem,
  InventoryImportParseResult,
  InventoryImportSavableItem
} from '../Types';
import { normalizeApiSearchOptions } from '../Utils/InventorySearch.utils';

const API_URL = import.meta.env.VITE_API_URL || 'https://dev.api.gioallday.com';

interface ApiInventorySearchRow {
  id: string;
  source: 'restaurant' | 'catalog';
  name: string;
  unit: string;
  unit_multiplier: number | null;
  base_unit: string | null;
  unit_definition: string | null;
  display_label: string;
  internal_sku: string | null;
  restaurant_inventory_item_id: string | null;
  catalog_item_id: string | null;
  variant_id: string | null;
  location_id: string | null;
  location_name: string | null;
  quantity_on_hand: number;
  min_quantity: number;
  stock_status: ExistingInventoryItem['stockStatus'];
}

interface ApiRestaurantItemRow {
  restaurant_inventory_item_id: string;
  stock_id: string;
  name: string;
  unit: string;
  unit_multiplier: number | null;
  base_unit: string | null;
  unit_definition: string | null;
  image_url: string | null;
  note: string | null;
  internal_sku: string | null;
  category_id?: string | null;
  category_name?: string | null;
  location_id: string | null;
  location_name: string | null;
  quantity_on_hand: number;
  min_quantity: number;
  min_order_qty?: number | null;
  stock_status: ExistingInventoryItem['stockStatus'];
  assigned_supplier_id: string | null;
}

interface UploadUrlResponse {
  key: string;
  uploadUrl: string;
  fileUrl: string;
  fileName: string;
  contentType?: string;
}

const authHeaders = (accessToken?: string | null): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  return headers;
};

const mapSearchRow = (row: ApiInventorySearchRow): ExistingInventoryItem => ({
  id: row.id,
  catalogItemId:
    row.catalog_item_id ?? row.restaurant_inventory_item_id ?? undefined,
  variantId: row.variant_id,
  name: row.name,
  unit: row.unit,
  displayLabel: row.display_label,
  internalSku: row.internal_sku,
  locationId: row.location_id,
  locationName: row.location_name,
  quantityOnHand: row.quantity_on_hand,
  minQuantity: row.min_quantity,
  stockStatus: row.stock_status,
  unitMultiplier: row.unit_multiplier,
  baseUnit: row.base_unit,
  unitDefinition: row.unit_definition
});

export const getInventoryAddPageContext = async (
  link: string
): Promise<InventoryAddPageContext> => {
  const response = await axios.get<{
    success: boolean;
    data: InventoryAddPageContext;
  }>(`${API_URL}/api/inventory/add-page/context`, {
    params: { link }
  });
  return response.data.data;
};

export const verifyInventoryAddLink = async (
  link: string
): Promise<{ accessToken: string }> => {
  const response = await axios.post<{ accessToken: string }>(
    `${API_URL}/api/inventory/add-link/verify`,
    { link }
  );
  return response.data;
};

export const generateInventoryAddLink = async (
  restaurantLocationId: string,
  options?: { accessToken?: string | null; supplierId?: string }
): Promise<InventoryAddLinkResponse> => {
  const body: { restaurant_location_id: string; supplier_id?: string } = {
    restaurant_location_id: restaurantLocationId
  };
  // Deprecated: server ignores supplier_id; kept so older callers can keep sending it.
  if (options?.supplierId) {
    body.supplier_id = options.supplierId;
  }

  const response = await axios.post<{
    success: boolean;
    data: InventoryAddLinkResponse;
  }>(`${API_URL}/api/inventory/restaurant-items/add-link`, body, {
    headers: authHeaders(options?.accessToken)
  });
  return response.data.data;
};

export const getInventoryUploadUrls = async (
  files: Array<{ fileName: string; contentType?: string }>,
  accessToken: string
): Promise<UploadUrlResponse[]> => {
  const response = await axios.post<UploadUrlResponse[]>(
    `${API_URL}/api/inventory/upload-urls`,
    { files, folder: 'inventory' },
    { headers: authHeaders(accessToken) }
  );
  return response.data;
};

export const uploadInventoryItemImages = async (
  items: InventoryItemDraft[],
  accessToken: string
): Promise<InventoryItemDraft[]> => {
  const pending = items.filter(item => item.imageFile);
  if (pending.length === 0) {
    return items;
  }

  const uploadUrls = await getInventoryUploadUrls(
    pending.map(item => ({
      fileName: item.imageName || item.imageFile!.name || 'image.jpg',
      contentType: item.imageFile!.type || undefined
    })),
    accessToken
  );

  await Promise.all(
    uploadUrls.map(async (meta, index) => {
      const file = pending[index].imageFile!;
      await axios.put(meta.uploadUrl, file, {
        headers: {
          'Content-Type':
            meta.contentType || file.type || 'application/octet-stream'
        }
      });
    })
  );

  const uploadedByLocalId = new Map(
    pending.map((item, index) => [item.localId, uploadUrls[index].fileUrl])
  );

  return items.map(item => ({
    ...item,
    imageUrl: uploadedByLocalId.get(item.localId) ?? item.imageUrl,
    imageFile: null
  }));
};

export const listStorageLocations = async (options?: {
  accessToken?: string | null;
  branchId?: string;
}): Promise<StorageLocationNode[]> => {
  const params: Record<string, string> = {};
  if (options?.branchId) params.restaurant_location_id = options.branchId;

  const response = await axios.get<{
    success: boolean;
    data: StorageLocationNode[];
  }>(`${API_URL}/api/inventory/locations`, {
    params,
    headers: authHeaders(options?.accessToken)
  });

  return response.data.data ?? [];
};

interface ApiCreatedStorageLocation {
  id: string;
  name: string;
  code?: string | null;
  locationType?: StorageLocationNode['location_type'];
  location_type?: StorageLocationNode['location_type'];
}

const mapCreatedStorageLocation = (
  row: ApiCreatedStorageLocation
): StorageLocationNode => ({
  id: row.id,
  name: row.name,
  code: row.code ?? null,
  location_type: row.location_type ?? row.locationType ?? 'room',
  sort_order: 0,
  active: true,
  children: []
});

export const createStorageLocation = async (
  payload: CreateStorageLocationPayload,
  options?: { accessToken?: string | null; branchId?: string }
): Promise<StorageLocationNode> => {
  const params: Record<string, string> = {};
  if (options?.branchId) params.restaurant_location_id = options.branchId;

  const body: Record<string, string> = {
    name: payload.name.trim(),
    locationType: payload.locationType
  };
  if (payload.code?.trim()) body.code = payload.code.trim();
  if (payload.parentLocationId)
    body.parentLocationId = payload.parentLocationId;

  const response = await axios.post<{
    success: boolean;
    data: ApiCreatedStorageLocation;
  }>(`${API_URL}/api/inventory/locations`, body, {
    params,
    headers: authHeaders(options?.accessToken)
  });

  return mapCreatedStorageLocation(response.data.data);
};

export interface RestaurantInventoryListMeta {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
  has_more: boolean;
}

export interface RestaurantInventoryListPage {
  data: ApiRestaurantItemRow[];
  count: number;
  meta?: RestaurantInventoryListMeta;
}

export const listRestaurantInventoryItems = async (options?: {
  accessToken?: string | null;
  branchId?: string;
  page?: number;
  limit?: number;
  search?: string;
}): Promise<ApiRestaurantItemRow[]> => {
  const page = await listRestaurantInventoryItemsPage(options);
  return page.data;
};

/** Paginated list — pass page/limit for fast web loads. Omit both for full list (legacy). */
export const listRestaurantInventoryItemsPage = async (options?: {
  accessToken?: string | null;
  branchId?: string;
  page?: number;
  limit?: number;
  search?: string;
}): Promise<RestaurantInventoryListPage> => {
  const params: Record<string, string | number> = {};
  if (options?.branchId) params.restaurant_location_id = options.branchId;
  if (options?.page != null) params.page = options.page;
  if (options?.limit != null) params.limit = options.limit;
  if (options?.search?.trim()) params.search = options.search.trim();

  const response = await axios.get<{
    success: boolean;
    data: ApiRestaurantItemRow[];
    count?: number;
    meta?: RestaurantInventoryListMeta;
  }>(`${API_URL}/api/inventory/restaurant-items`, {
    params,
    headers: authHeaders(options?.accessToken)
  });

  return {
    data: response.data.data ?? [],
    count: response.data.count ?? response.data.data?.length ?? 0,
    meta: response.data.meta
  };
};

/** Loads all pages (50/page) for web onboarding — first page is available immediately via onPage. */
export const listAllRestaurantInventoryItemsPaged = async (
  options: {
    accessToken?: string | null;
    branchId?: string;
    pageSize?: number;
    onPage?: (rows: ApiRestaurantItemRow[], meta: RestaurantInventoryListMeta) => void;
  }
): Promise<ApiRestaurantItemRow[]> => {
  const pageSize = options.pageSize ?? 50;
  const first = await listRestaurantInventoryItemsPage({
    accessToken: options.accessToken,
    branchId: options.branchId,
    page: 1,
    limit: pageSize
  });

  const mergeUnique = (
    existing: ApiRestaurantItemRow[],
    incoming: ApiRestaurantItemRow[]
  ): ApiRestaurantItemRow[] => {
    const seen = new Set(
      existing
        .map(row => row.stock_id || row.restaurant_inventory_item_id)
        .filter(Boolean)
    );
    const next = [...existing];
    for (const row of incoming) {
      const key = row.stock_id || row.restaurant_inventory_item_id;
      if (key && seen.has(key)) continue;
      if (key) seen.add(key);
      next.push(row);
    }
    return next;
  };

  let all = [...first.data];
  if (first.meta) {
    options.onPage?.(first.data, first.meta);
  }

  // Legacy / misconfigured servers may return the full list on page 1.
  // Stop early so we don't re-fetch and duplicate rows.
  const reportedTotal = first.meta?.total;
  if (
    !first.meta ||
    first.data.length < pageSize ||
    (reportedTotal != null && all.length >= reportedTotal)
  ) {
    return all;
  }

  const totalPages = first.meta.total_pages ?? 1;
  for (let page = 2; page <= totalPages; page += 1) {
    const next = await listRestaurantInventoryItemsPage({
      accessToken: options.accessToken,
      branchId: options.branchId,
      page,
      limit: pageSize
    });
    all = mergeUnique(all, next.data);
    if (next.meta) {
      options.onPage?.(next.data, {
        ...next.meta,
        // Keep UI progress honest even if a page was partially deduped.
        has_more: page < totalPages && all.length < (reportedTotal ?? Number.POSITIVE_INFINITY)
      });
    }
    if (reportedTotal != null && all.length >= reportedTotal) {
      break;
    }
    if (next.data.length < pageSize) {
      break;
    }
  }

  return all;
};

export const searchRestaurantInventoryItems = async (
  search: string,
  options?: { accessToken?: string | null; branchId?: string }
): Promise<ExistingInventoryItem[]> => {
  const params: Record<string, string> = {};
  if (search.trim()) params.search = search.trim();
  if (options?.branchId) params.restaurant_location_id = options.branchId;

  const response = await axios.get<{
    success: boolean;
    data: ApiInventorySearchRow[];
  }>(`${API_URL}/api/inventory/item-search`, {
    params,
    headers: authHeaders(options?.accessToken)
  });

  return normalizeApiSearchOptions(
    (response.data.data ?? []).map(mapSearchRow)
  );
};

export interface BulkCreatedInventoryItemResult {
  restaurantInventoryItemId: string;
  stockId: string;
  name: string;
  unit: string;
  assignedSupplierId: string | null;
}

export interface BulkSkippedInventoryItem {
  name: string;
  unit: string;
  reason: 'already_exists';
  restaurantInventoryItemId: string | null;
}

export interface BulkCreateRestaurantInventoryItemsResult {
  created: BulkCreatedInventoryItemResult[];
  skipped: BulkSkippedInventoryItem[];
}

export const bulkCreateRestaurantInventoryItems = async (
  payload: BulkCreateInventoryPayload,
  options?: { accessToken?: string | null; branchId?: string }
): Promise<BulkCreatedInventoryItemResult[]> => {
  const result = await bulkCreateRestaurantInventoryItemsDetailed(
    payload,
    options
  );
  return result.created;
};

export const bulkCreateRestaurantInventoryItemsDetailed = async (
  payload: BulkCreateInventoryPayload,
  options?: { accessToken?: string | null; branchId?: string }
): Promise<BulkCreateRestaurantInventoryItemsResult> => {
  const params: Record<string, string> = {};
  if (options?.branchId) params.restaurant_location_id = options.branchId;

  type CreatedRow = {
    restaurant_inventory_item?: { id?: string; name?: string; unit?: string };
    stock?: { id?: string };
    supplier_links?: Array<{ supplier_id?: string }>;
  };

  const response = await axios.post<{
    success?: boolean;
    data?: CreatedRow[];
    skipped?: Array<{
      name?: string;
      unit?: string;
      reason?: string;
      restaurant_inventory_item_id?: string | null;
    }>;
  }>(
    `${API_URL}/api/inventory/restaurant-items/bulk`,
    {
      items: payload.items.map(item => ({
        name: item.name,
        unit: item.unit,
        locationId: item.locationId,
        quantityOnHand: item.quantityOnHand,
        minQuantity: item.minQuantity,
        internalSku: item.internalSku,
        pinCode: item.pinCode,
        unitMultiplier: item.unitMultiplier,
        baseUnit: item.baseUnit,
        note: item.note,
        imageUrl: item.imageUrl,
        supplierUserId: item.supplierUserId,
        categoryId: item.categoryId,
        restaurantInventoryItemId: item.restaurantInventoryItemId
      })),
      pinCode: payload.pinCode
    },
    { params, headers: authHeaders(options?.accessToken) }
  );

  const created = (response.data?.data ?? [])
    .map(row => {
      const itemId = row.restaurant_inventory_item?.id?.trim() ?? '';
      const stockId = row.stock?.id?.trim() ?? '';
      if (!itemId || !stockId) return null;
      return {
        restaurantInventoryItemId: itemId,
        stockId,
        name: row.restaurant_inventory_item?.name?.trim() ?? '',
        unit: row.restaurant_inventory_item?.unit?.trim() ?? '',
        assignedSupplierId: row.supplier_links?.[0]?.supplier_id?.trim() || null
      };
    })
    .filter((row): row is BulkCreatedInventoryItemResult => Boolean(row));

  const skipped = (response.data?.skipped ?? [])
    .map(row => {
      const name = row.name?.trim() ?? '';
      const unit = row.unit?.trim() ?? '';
      if (!name || !unit) return null;
      return {
        name,
        unit,
        reason: 'already_exists' as const,
        restaurantInventoryItemId:
          row.restaurant_inventory_item_id?.trim() || null
      };
    })
    .filter((row): row is BulkSkippedInventoryItem => Boolean(row));

  return { created, skipped };
};

export const deleteRestaurantInventoryStock = async (
  stockId: string,
  options?: { accessToken?: string | null; branchId?: string }
): Promise<void> => {
  const params: Record<string, string> = {};
  if (options?.branchId) params.restaurant_location_id = options.branchId;

  await axios.delete(
    `${API_URL}/api/inventory/restaurant-items/stocks/${stockId}`,
    {
      params,
      headers: authHeaders(options?.accessToken)
    }
  );
};

export interface UpdateRestaurantInventoryItemPayload {
  name?: string;
  unit?: string;
  locationId?: string;
  quantityOnHand?: number;
  minQuantity?: number;
  minOrderQty?: number;
  unitMultiplier?: number;
  baseUnit?: string;
  note?: string;
  imageUrl?: string;
  supplierUserId?: string;
  categoryId?: string;
  clearCategory?: boolean;
}

export const updateRestaurantInventoryItem = async (
  restaurantInventoryItemId: string,
  payload: UpdateRestaurantInventoryItemPayload,
  options?: { accessToken?: string | null; branchId?: string }
): Promise<void> => {
  const params: Record<string, string> = {};
  if (options?.branchId) params.restaurant_location_id = options.branchId;

  await axios.patch(
    `${API_URL}/api/inventory/restaurant-items/${restaurantInventoryItemId}`,
    payload,
    {
      params,
      headers: authHeaders(options?.accessToken)
    }
  );
};

export interface BulkAssignedSupplierResult {
  restaurantInventoryItemId: string;
  supplierUserId: string;
}

export const bulkAssignRestaurantInventorySupplier = async (
  restaurantInventoryItemIds: string[],
  supplierUserId: string,
  options?: { accessToken?: string | null; branchId?: string }
): Promise<BulkAssignedSupplierResult[]> => {
  const params: Record<string, string> = {};
  if (options?.branchId) params.restaurant_location_id = options.branchId;

  const response = await axios.patch<{
    success: boolean;
    data: Array<{
      restaurant_inventory_item_id: string;
      supplier_user_id: string;
    }>;
  }>(
    `${API_URL}/api/inventory/restaurant-items/bulk-supplier`,
    { restaurantInventoryItemIds, supplierUserId },
    { params, headers: authHeaders(options?.accessToken) }
  );

  return response.data.data.map(row => ({
    restaurantInventoryItemId: row.restaurant_inventory_item_id,
    supplierUserId: row.supplier_user_id
  }));
};

export interface RelocatedInventoryStockResult {
  sourceStockId: string;
  stockId: string;
  locationId: string;
  locationName: string;
  quantityOnHand: number;
}

export const relocateRestaurantInventoryStock = async (
  stockId: string,
  locationId: string,
  options?: { accessToken?: string | null; branchId?: string }
): Promise<RelocatedInventoryStockResult> => {
  const params: Record<string, string> = {};
  if (options?.branchId) params.restaurant_location_id = options.branchId;

  const response = await axios.patch<{
    success: boolean;
    data: {
      source_stock_id: string;
      stock_id: string;
      location_id: string;
      location_name: string;
      quantity_on_hand: number;
    };
  }>(
    `${API_URL}/api/inventory/restaurant-items/stocks/${stockId}/location`,
    { locationId },
    { params, headers: authHeaders(options?.accessToken) }
  );

  return {
    sourceStockId: response.data.data.source_stock_id,
    stockId: response.data.data.stock_id,
    locationId: response.data.data.location_id,
    locationName: response.data.data.location_name,
    quantityOnHand: Number(response.data.data.quantity_on_hand ?? 0)
  };
};

export const bulkRelocateRestaurantInventoryStocks = async (
  stockIds: string[],
  locationId: string,
  options?: { accessToken?: string | null; branchId?: string }
): Promise<RelocatedInventoryStockResult[]> => {
  const params: Record<string, string> = {};
  if (options?.branchId) params.restaurant_location_id = options.branchId;

  const response = await axios.patch<{
    success: boolean;
    data: Array<{
      source_stock_id: string;
      stock_id: string;
      location_id: string;
      location_name: string;
      quantity_on_hand: number;
    }>;
  }>(
    `${API_URL}/api/inventory/restaurant-items/stocks/bulk-location`,
    { stockIds, locationId },
    { params, headers: authHeaders(options?.accessToken) }
  );

  return response.data.data.map(row => ({
    sourceStockId: row.source_stock_id,
    stockId: row.stock_id,
    locationId: row.location_id,
    locationName: row.location_name,
    quantityOnHand: Number(row.quantity_on_hand ?? 0)
  }));
};

interface ApiInventoryCategoryRow {
  id?: string;
  categoryId?: string;
  category_id?: string;
  name?: string;
  categoryName?: string;
  category_name?: string;
  sort_order?: number;
  sortOrder?: number;
}

const mapCategoryRow = (
  row: ApiInventoryCategoryRow
): InventoryItemCategory | null => {
  const id = (row.id ?? row.categoryId ?? row.category_id ?? '').trim();
  const name = (row.name ?? row.categoryName ?? row.category_name ?? '').trim();
  if (!id || !name) return null;
  return {
    id,
    name,
    sortOrder: row.sortOrder ?? row.sort_order ?? 0
  };
};

export const listInventoryItemCategories = async (options?: {
  accessToken?: string | null;
  branchId?: string;
}): Promise<InventoryItemCategory[]> => {
  const params: Record<string, string> = {};
  if (options?.branchId) params.restaurant_location_id = options.branchId;

  const response = await axios.get<{
    success?: boolean;
    data?:
    | ApiInventoryCategoryRow[]
    | { categories?: ApiInventoryCategoryRow[] };
    categories?: ApiInventoryCategoryRow[];
  }>(`${API_URL}/api/inventory/item-categories`, {
    params,
    headers: authHeaders(options?.accessToken)
  });

  const payload = response.data;
  const raw =
    (Array.isArray(payload?.data) && payload.data) ||
    (payload?.data &&
      typeof payload.data === 'object' &&
      Array.isArray(
        (payload.data as { categories?: ApiInventoryCategoryRow[] }).categories
      ) &&
      (payload.data as { categories: ApiInventoryCategoryRow[] }).categories) ||
    (Array.isArray(payload?.categories) && payload.categories) ||
    [];

  return raw
    .map(mapCategoryRow)
    .filter((row): row is InventoryItemCategory => Boolean(row))
    .sort((a, b) => {
      const order = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      if (order !== 0) return order;
      return a.name.localeCompare(b.name);
    });
};

export const createInventoryItemCategory = async (
  name: string,
  options?: { accessToken?: string | null; branchId?: string }
): Promise<InventoryItemCategory> => {
  const params: Record<string, string> = {};
  if (options?.branchId) params.restaurant_location_id = options.branchId;

  const response = await axios.post<{
    success?: boolean;
    data?: ApiInventoryCategoryRow;
  }>(
    `${API_URL}/api/inventory/item-categories`,
    { name: name.trim() },
    { params, headers: authHeaders(options?.accessToken) }
  );

  const mapped = mapCategoryRow(
    response.data?.data ?? (response.data as ApiInventoryCategoryRow)
  );
  if (!mapped) {
    throw new Error('Invalid response from server');
  }
  return mapped;
};

export const listLocationSuppliers = async (
  locationId: string,
  accessToken: string
): Promise<LocationSupplier[]> => {
  const response = await axios.get<{
    success: boolean;
    data: LocationSupplier[];
  }>(`${API_URL}/api/users/restaurant-locations/${locationId}/suppliers`, {
    headers: authHeaders(accessToken)
  });
  return (response.data.data ?? []).filter(s => s.active && !s.is_archived);
};

export const addOutOfNetworkSupplier = async (
  locationId: string,
  payload: AddOutOfNetworkSupplierPayload,
  accessToken: string
): Promise<LocationSupplier> => {
  const response = await axios.post<{
    success: boolean;
    data: {
      supplier_id: string;
      business_name: string;
      network_type: LocationSupplier['network_type'];
      email: string | null;
      phone: string | null;
    };
  }>(
    `${API_URL}/api/users/restaurant-locations/${locationId}/suppliers/out-of-network`,
    payload,
    { headers: authHeaders(accessToken) }
  );

  const data = response.data?.data;
  if (!data?.supplier_id) {
    throw new Error('Invalid response from server');
  }
  return {
    supplier_id: data.supplier_id,
    business_name: data.business_name,
    network_type: data.network_type,
    email: data.email,
    phone: data.phone,
    contact_person_name: payload.contact_person_name ?? null,
    is_archived: false,
    active: true
  };
};

/** Parse inventory from a spreadsheet file and/or pasted text (does not save).
 * Backend: POST /api/inventory/import/parse — send `file` XOR `text`.
 */
export const parseInventoryImport = async (
  input: { file?: File | null; text?: string | null },
  accessToken?: string | null
): Promise<InventoryImportParseResult> => {
  const file = input.file ?? null;
  const text = input.text?.trim() ?? '';

  if (file && text) {
    throw new Error('Send either a file or text — not both.');
  }
  if (!file && !text) {
    throw new Error('Provide a spreadsheet file or paste inventory text.');
  }

  if (file) {
    const form = new FormData();
    form.append('file', file);
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    const response = await axios.post<{
      success?: boolean;
      data?: InventoryImportParseResult;
    }>(`${API_URL}/api/inventory/import/parse`, form, { headers });
    const data = response.data?.data;
    if (!data?.items) {
      throw new Error('Invalid response from server');
    }
    return normalizeImportParseResult(data);
  }

  const response = await axios.post<{
    success?: boolean;
    data?: InventoryImportParseResult;
  }>(
    `${API_URL}/api/inventory/import/parse`,
    { text },
    { headers: authHeaders(accessToken) }
  );
  const data = response.data?.data;
  if (!data?.items) {
    throw new Error('Invalid response from server');
  }
  return normalizeImportParseResult(data);
};

/** @deprecated Prefer parseInventoryImport({ file }) */
export const parseInventoryImportFile = async (
  file: File,
  accessToken?: string | null
): Promise<InventoryImportParseResult> =>
  parseInventoryImport({ file }, accessToken);

/** @deprecated Prefer parseInventoryImport({ text }) */
export const parseInventoryImportText = async (
  text: string,
  accessToken?: string | null
): Promise<InventoryImportParseResult> =>
  parseInventoryImport({ text }, accessToken);

const normalizeImportParseResult = (
  data: InventoryImportParseResult
): InventoryImportParseResult => {
  return {
    ...data,
    items: data.items.map(row => {
      const item = row.item ?? (row as unknown as InventoryImportSavableItem);
      const minRaw = item.minQuantity;
      const minQuantity =
        minRaw != null && Number.isFinite(Number(minRaw)) && Number(minRaw) >= 0
          ? Number(minRaw)
          : 0;
      return {
        item: {
          name: toTitleCaseName(String(item.name ?? '')),
          unit: String(item.unit ?? 'unit').trim() || 'unit',
          minQuantity,
          ...(item.locationId ? { locationId: item.locationId } : {}),
          ...(item.quantityOnHand != null
            ? { quantityOnHand: Number(item.quantityOnHand) }
            : {}),
          ...(item.internalSku ? { internalSku: String(item.internalSku) } : {}),
          ...(item.unitMultiplier != null && item.baseUnit
            ? {
                unitMultiplier: Number(item.unitMultiplier),
                baseUnit: String(item.baseUnit)
              }
            : {}),
          ...(item.note ? { note: String(item.note) } : {}),
          ...(item.imageUrl ? { imageUrl: String(item.imageUrl) } : {}),
          ...(item.supplierUserId
            ? { supplierUserId: String(item.supplierUserId) }
            : {}),
          ...(item.categoryId ? { categoryId: String(item.categoryId) } : {})
        },
        categoryName: row.categoryName ?? null,
        categorySuggested: Boolean(row.categorySuggested),
        confidence: row.confidence ?? 'medium',
        needsReview: Boolean(row.needsReview),
        rawSnippet: row.rawSnippet ?? null
      } satisfies ImportedInventoryItem;
    })
  };
};

/** Proper English title case for imported item names. */
export const toTitleCaseName = (raw: string): string =>
  raw
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(word =>
      word
        .split('-')
        .map(part => {
          if (!part) return part;
          return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        })
        .join('-')
    )
    .join(' ');


